import { useEffect, useRef } from 'react';
import { monthOfDay, type MonthAnchor } from './model/days';
import { WEEKDAY_HEADER_HEIGHT } from './components/CalendarSurface';

/**
 * Jak daleko od hrany se spustí donačtení. Rezerva je záměrně velká — než dotaz doletí a měsíce
 * se vykreslí, uživatel při rychlém posuvu urazí pěkný kus, a bez předstihu by narazil do prázdna.
 */
const EDGE_MARGIN_PX = 900;

export interface CalendarScrollOptions {
  /** Posuvná plocha kalendáře. */
  scroller: HTMLDivElement | null;
  isTimeline: boolean;
  /** První den načteného okna — osa z něj počítá viditelný měsíc aritmeticky. */
  rangeStart: number;
  /** Šířka dne v px (jen osa). */
  dayWidth: number;
  /**
   * Klíč načteného okna. Po jeho změně se uvolní pojistka, která brání tomu, aby jedno doscrollování
   * k hraně spustilo donačtení několikrát za sebou.
   */
  windowKey: string;
  /**
   * Sledování hran je vypnuté, dokud kalendář nedoscrolluje na výchozí měsíc — jinak by hned po
   * připojení (posuv na nule) donačetl měsíce dozadu a odskočil.
   */
  enabled: boolean;
  onReachStart: () => void;
  onReachEnd: () => void;
  onVisibleMonthChange: (month: MonthAnchor) => void;
}

/** Měsíc, na kterém stojí horní hrana výřezu (těsně pod sticky hlavičkou PO–NE). */
function visibleMonthInWeeks(scroller: HTMLDivElement): MonthAnchor | null {
  const line = scroller.scrollTop + WEEKDAY_HEADER_HEIGHT + 1;
  const blocks = scroller.querySelectorAll<HTMLElement>('[data-month]');
  let current: string | null = null;
  for (const block of blocks) {
    if (block.offsetTop > line) break;
    current = block.getAttribute('data-month');
  }
  if (current === null) current = blocks[0]?.getAttribute('data-month') ?? null;
  if (current === null) return null;
  // `data-month` je ordinální index měsíce (rok × 12 + měsíc), viz `buildCalendarLayout`.
  const index = Number(current);
  return { year: Math.floor(index / 12), month: ((index % 12) + 12) % 12 };
}

/**
 * Řídí posuv kalendáře: hlídá hrany (a hlásí, že je čas donačíst měsíce) a sleduje, na kterém
 * měsíci výřez stojí.
 *
 * Obojí visí na **jednom** posluchači `scroll` sraženém do `requestAnimationFrame` a **nic z toho
 * nedrží ve stavu Reactu**. Callbacky se volají jen tehdy, když se něco opravdu změní — jinak by
 * každý scroll event překreslil celou plochu kalendáře.
 */
export function useCalendarScroll({
  scroller,
  isTimeline,
  rangeStart,
  dayWidth,
  windowKey,
  enabled,
  onReachStart,
  onReachEnd,
  onVisibleMonthChange,
}: CalendarScrollOptions): void {
  // Callbacky přes ref, ať jejich identita nepřepojuje posluchač při každém renderu.
  const handlers = useRef({ onReachStart, onReachEnd, onVisibleMonthChange });
  handlers.current = { onReachStart, onReachEnd, onVisibleMonthChange };

  /**
   * Pojistka proti tomu, aby jedno doscrollování k hraně spustilo donačtení několikrát.
   *
   * Uvolní ji teprve nové okno — a i to až v **dalším snímku**. Bez toho odkladu by se řetěz uzavřel
   * synchronně: nové okno změní `rangeStart`, tím se přepojí posluchač níž, ten hned zavolá `read()`,
   * ten je pořád u hrany → další donačtení, a tak pořád dokola v jednom commitu, dokud React
   * nespadne na „Maximum update depth exceeded“. S odkladem se každé kolo opře o skutečnou událost
   * `scroll` (posuv se po doplnění měsíců vždycky posune, ať už nativním ukotvením, nebo ruční
   * korekcí u osy), takže načítání drží krok s tím, jak uživatel opravdu scrolluje.
   */
  const loadingRef = useRef(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      loadingRef.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [windowKey]);

  const lastMonthRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scroller) return;
    let frame = 0;

    const read = () => {
      frame = 0;

      const month = isTimeline
        ? monthOfDay(Math.floor(rangeStart + scroller.scrollLeft / (dayWidth || 1)))
        : visibleMonthInWeeks(scroller);
      if (month) {
        const key = `${month.year}-${month.month}`;
        if (key !== lastMonthRef.current) {
          lastMonthRef.current = key;
          handlers.current.onVisibleMonthChange(month);
        }
      }

      if (!enabled || loadingRef.current) return;
      const position = isTimeline ? scroller.scrollLeft : scroller.scrollTop;
      const size = isTimeline ? scroller.scrollWidth : scroller.scrollHeight;
      const client = isTimeline ? scroller.clientWidth : scroller.clientHeight;

      if (position < EDGE_MARGIN_PX) {
        loadingRef.current = true;
        handlers.current.onReachStart();
      } else if (size - client - position < EDGE_MARGIN_PX) {
        loadingRef.current = true;
        handlers.current.onReachEnd();
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };

    read();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    observer?.observe(scroller);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [scroller, isTimeline, rangeStart, dayWidth, enabled]);
}
